import React, { useEffect, useRef } from 'react'
import { loadExternalScript } from './loadExternalScript'
import '../../styles/TrustSignalsModule/TrustpilotBadge.scss'

const TRUSTPILOT_SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'

// The bootstrap injects its iframe as soon as it runs, so a container still empty
// after this means the widget rendered nothing.
const RENDER_DEADLINE_MS = 3000
const RENDER_POLL_MS = 100

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement, forceReload?: boolean) => void
    }
  }
}

export interface TrustpilotBadgeProps {
  // Must be referentially stable -- the load effect re-runs when it changes.
  onResult: (loaded: boolean) => void
}

export const TrustpilotBadge = ({ onResult }: TrustpilotBadgeProps) => {
  const widgetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    let timeoutId: number | null = null

    loadExternalScript(TRUSTPILOT_SCRIPT_SRC).then(() => {
      if (cancelled || widgetRef.current === null) return
      // The bootstrap scans for widget containers when it first runs; forcing a reload here
      // would tear down the iframe that scan already rendered and render it again.
      // The vendor call is third-party: a throw would otherwise strand the badge staged, so
      // treat it as a failed render and drop the badge.
      try {
        window.Trustpilot?.loadFromElement(widgetRef.current, false)
      } catch {
        if (!cancelled) onResult(false)
        return
      }

      const deadline = Date.now() + RENDER_DEADLINE_MS
      const check = () => {
        if (cancelled || widgetRef.current === null) return
        if (widgetRef.current.querySelector('iframe') !== null) {
          onResult(true)
        } else if (Date.now() >= deadline) {
          onResult(false)
        } else {
          timeoutId = window.setTimeout(check, RENDER_POLL_MS)
        }
      }
      check()
    }, () => {
      if (!cancelled) onResult(false)
    })

    return () => {
      cancelled = true
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }, [onResult])

  return (
    <div className="trust-signals__trustpilot-wrapper">
      <div
        ref={widgetRef}
        className="trustpilot-widget trust-signals__trustpilot"
        data-locale="en-US"
        data-template-id="53aa8807dec7e10d38f59f32"
        data-businessunit-id="5d0a4f589bed450001df3af5"
        // The widget clips its score row below about 100px, so this bound is the widget's
        // own content, not the surrounding slot.
        data-style-height="100px"
        data-style-width="100%"
        data-theme="light"
        data-token="2803af06-5cd1-4737-89e2-de0c191c3a70"
      />
    </div>
  )
}
